import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Job } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import UnassignWorkerModal from '../../UnassignWorkerModal';
import AssignedWorkerCard from './AssignedWorkerCard';

interface AssignedWorkerProps {
  job: Job;
  allowUnassign?: boolean;
  showWorkerPayRate?: boolean;
}

const AssignedWorker = ({
  job,
  allowUnassign = true,
  showWorkerPayRate = true,
}: AssignedWorkerProps) => {
  const { fetchWorkerById, getWorkerById } = useWorkerStore();

  const [loading, setLoading] = useState(false);

  const [openUnassignWorkerModal, setOpenUnassignWorkerModal] = useState(false);

  const { unassignWorkerFromJob } = useJobStore();

  const { getTerm } = useTemplateStore();

  const worker = job?.userId ? getWorkerById(job.userId) : null;

  // Fetch the assigned worker if it is not in the store.
  useEffect(() => {
    if (!job?.userId) return;

    const existingWorker = useWorkerStore
      .getState()
      .workers.find((worker) => worker.id === job.userId);
    if (existingWorker) return;

    void fetchWorkerById(job.userId);
  }, [job?.userId, fetchWorkerById]);

  const handleUnassignWorker = useCallback(async () => {
    try {
      // Set as loading. Gives user feedback that the operation is in progress.
      setLoading(true);

      // Get the worker's name to display in the success modal.
      const workerFullName = `${worker?.firstName} ${worker?.lastName || ''}`;

      // Unassign the worker from the job.
      await unassignWorkerFromJob(job.id);

      // Show the success toast.
      toast.success(
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {getTerm(TemplateTerm.WORKER, false)} has been removed
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {`${workerFullName} is no longer assigned to this job.`}
          </p>
        </div>,
        {
          position: 'top-right',
          autoClose: 5000,
          closeButton: false,
          className:
            '!border !border-green-200 !bg-green-50 !p-4 !rounded-lg !shadow-lg !min-w-[400px]',
        },
      );
    } catch (error) {
      toast.error('Error unassigning worker from job');
      console.error('Error unassigning worker from job: ', error);
    } finally {
      // Close the unassign worker modal and set as not loading.
      setOpenUnassignWorkerModal(false);
      setLoading(false);
    }
  }, [job.id, worker, unassignWorkerFromJob]);

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          {getTerm(TemplateTerm.WORKER, false)}
        </h3>
      </div>

      <AssignedWorkerCard
        job={job}
        worker={worker}
        loading={loading}
        setOpenUnassignWorkerModal={setOpenUnassignWorkerModal}
        allowUnassign={allowUnassign}
        showWorkerPayRate={showWorkerPayRate}
      />

      {allowUnassign && (
        <UnassignWorkerModal
          workerFullName={`${worker?.firstName} ${worker?.lastName || ''}`}
          isOpen={openUnassignWorkerModal}
          onClose={() => setOpenUnassignWorkerModal(false)}
          onUnassignWorker={handleUnassignWorker}
        />
      )}
    </>
  );
};

export default AssignedWorker;
